import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { track } from '@/lib/analytics/bus';

interface Props {
  companyId: string;
  /** Patient ids currently linked to the company (active assignments) */
  patientIds: string[];
  /** Days without activity to be considered inactive */
  thresholdDays?: number;
}

interface InactivePatient {
  patient_id: string;
  nickname: string;
  last_check_in_at: string | null;
}

export function InactivePatientsCard({ companyId, patientIds, thresholdDays = 60 }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const { data: inactive = [], isLoading } = useQuery({
    queryKey: ['company-inactive-patients', companyId, patientIds.length, thresholdDays],
    enabled: !!companyId && patientIds.length > 0,
    queryFn: async (): Promise<InactivePatient[]> => {
      // Fetch patient names + status to filter out already archived
      const { data: patients } = await supabase
        .from('patients')
        .select('id, nickname, status')
        .in('id', patientIds);

      const activePatients = (patients || []).filter((p: any) => p.status !== 'archived');
      if (!activePatients.length) return [];

      const ids = activePatients.map(p => p.id);

      // Latest check_in_at per patient (any shift status)
      const { data: shifts } = await supabase
        .from('shifts')
        .select('patient_id, check_in_at')
        .in('patient_id', ids)
        .not('check_in_at', 'is', null)
        .order('check_in_at', { ascending: false });

      const lastByPatient = new Map<string, string>();
      (shifts || []).forEach((s: any) => {
        if (!lastByPatient.has(s.patient_id) && s.check_in_at) {
          lastByPatient.set(s.patient_id, s.check_in_at);
        }
      });

      const cutoff = Date.now() - thresholdDays * 86400 * 1000;
      const result: InactivePatient[] = activePatients
        .map((p: any) => {
          const last = lastByPatient.get(p.id) || null;
          return { patient_id: p.id, nickname: p.nickname, last_check_in_at: last };
        })
        .filter(p => !p.last_check_in_at || new Date(p.last_check_in_at).getTime() < cutoff)
        .sort((a, b) => {
          const aTs = a.last_check_in_at ? new Date(a.last_check_in_at).getTime() : 0;
          const bTs = b.last_check_in_at ? new Date(b.last_check_in_at).getTime() : 0;
          return aTs - bTs;
        });

      return result;
    },
  });

  useEffect(() => {
    if (!isLoading && inactive.length > 0) {
      track('inactive_patient_card_viewed', { count: inactive.length, threshold_days: thresholdDays });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, inactive.length]);

  if (isLoading || inactive.length === 0) return null;

  const visible = inactive.slice(0, 5);

  return (
    <Card className="border-warning/40 bg-warning/5">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-warning" />
          {t('company.inactivePatients.title', { defaultValue: 'Pacientes sem atividade recente' })}
          <Badge variant="secondary">{inactive.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {t('company.inactivePatients.subtitle', {
            days: thresholdDays,
            defaultValue:
              'Sem plantão registrado nos últimos {{days}} dias. Considere arquivar para liberar slots da assinatura.',
          })}
        </p>
        <div className="space-y-1.5">
          {visible.map((p) => (
            <button
              key={p.patient_id}
              onClick={() => {
                track('inactive_patient_card_action', { patient_id: p.patient_id, action: 'review' });
                navigate(`/app/patients/${p.patient_id}`);
              }}
              className="w-full flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 hover:border-primary/40 transition-colors text-left"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{p.nickname}</p>
                <p className="text-xs text-muted-foreground">
                  {p.last_check_in_at
                    ? t('company.inactivePatients.lastShift', {
                        when: formatDistanceToNow(new Date(p.last_check_in_at), { locale, addSuffix: true }),
                        defaultValue: 'Último plantão {{when}}',
                      })
                    : t('company.inactivePatients.noShifts', { defaultValue: 'Nenhum plantão registrado' })}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
        {inactive.length > visible.length && (
          <p className="text-xs text-muted-foreground text-center pt-1">
            {t('company.inactivePatients.more', {
              count: inactive.length - visible.length,
              defaultValue: '+{{count}} outros',
            })}
          </p>
        )}
        <div className="pt-1">
          <Button
            size="sm"
            variant="ghost"
            className="w-full text-xs"
            onClick={() => {
              track('inactive_patient_card_action', { action: 'view_all' });
              navigate('/app/patients');
            }}
          >
            {t('company.inactivePatients.viewAll', { defaultValue: 'Ver todos os pacientes' })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
